import { useCallback, useMemo, useState } from 'react'

import { clsx } from 'clsx'

import { EmptyState, Flex, MessagesSquareIcon } from '@buffer-mono/popcorn'

import { useChannelMap } from '../hooks/useChannelMap'
import { ConversationHeader } from './ConversationHeader'
import { ConversationsSidepanel } from './ConversationsSidepanel'
import { DetailsPanel } from './DetailsPanel'
import { supportsDms } from './dmsCapabilities'
import { useAutoSelectConversation } from './hooks/useAutoSelectConversation'
import { useConversationMessagesQuery } from './hooks/useConversationMessagesQuery'
import { useConversationsQuery } from './hooks/useConversationsQuery'
import { useResolveConversation } from './hooks/useResolveConversation'
import { getCounterpart } from './ConversationCard/conversationDisplay'
import { MessageComposer } from './MessageComposer'
import { getReplyWindowExpiresAt } from './MessageComposer/replyWindow'
import { MessageThread } from './MessageThread'
import { useSelectedConversation } from './hooks/useSelectedConversation'
import { REPLAY_BLOCK_CLASS } from './replayPrivacy'
import type { DMsChannel } from './types'

import styles from './DMsView.module.css'

import type { JSX } from 'react'

type DMsViewProps = {
  /**
   * The page scope's channels: every viewable channel on All Channels, the
   * one channel on a single-channel page. The view keeps the ones that can
   * hold DMs; the inbox query fails as a whole for any that cannot.
   */
  channels: ReadonlyArray<DMsChannel>
}

/**
 * The Messages view: conversation list, message thread, and an optional
 * details pane.
 *
 * Three panes rather than the single feed the Comments and Mentions tabs use,
 * because the unit here is a conversation that contains messages, not an item
 * that stands alone.
 *
 * The list reads the conversations inbox; the thread header, the composer and
 * the details pane read the selected row from that same list, so they all
 * agree; the thread reads that conversation's messages. Only a conversation
 * the list holds is loaded: a selected id it does not hold shows the loading
 * state while the inbox loads, then gives way (`useAutoSelectConversation`).
 * The composer renders once the row is known, since the reply window lives
 * on it. Resolving from the header or a card's menu updates that same row
 * through the cache.
 *
 * The whole view is blocked in session replay recordings: its conversations
 * are private.
 */
export function DMsView({ channels }: DMsViewProps): JSX.Element {
  const { selectedConversationId, selectConversation, clearSelection } =
    useSelectedConversation()
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  const dmChannels = useMemo(() => channels.filter(supportsDms), [channels])
  const channelById = useChannelMap(dmChannels)
  const {
    conversations,
    loading,
    error,
    fetchingMore,
    lastElementRef,
    settled,
    refetch,
  } = useConversationsQuery({ channels: dmChannels })

  useAutoSelectConversation({
    conversations,
    settled,
    selectedConversationId,
    selectConversation,
    clearSelection,
  })

  // `undefined` until the page that holds the row has loaded; the header
  // shows a skeleton in the meantime.
  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.id === selectedConversationId,
      ),
    [conversations, selectedConversationId],
  )
  // The conversation the thread pane is for: the selected row, or the
  // selected id while the inbox that may hold it is still loading.
  const paneConversationId =
    selectedConversation?.id ?? (loading ? selectedConversationId : undefined)

  const { resolve, unresolve } = useResolveConversation()
  const resolveConversation = useCallback(
    (conversationId: string): void => {
      void resolve(conversationId)
    },
    [resolve],
  )
  const unresolveConversation = useCallback(
    (conversationId: string): void => {
      void unresolve(conversationId)
    },
    [unresolve],
  )

  const thread = useConversationMessagesQuery({
    conversationId: selectedConversation?.id,
  })

  const closeDetails = useCallback((): void => setIsDetailsOpen(false), [])
  const toggleDetails = useCallback(
    (): void => setIsDetailsOpen((open) => !open),
    [],
  )
  const retry = useCallback((): void => {
    void refetch()
  }, [refetch])
  const retryThread = useCallback((): void => {
    void thread.refetch()
  }, [thread])

  return (
    <div
      className={clsx(styles.container, REPLAY_BLOCK_CLASS)}
      data-testid="dms-view"
    >
      <ConversationsSidepanel
        conversations={conversations}
        loading={loading}
        error={error}
        fetchingMore={fetchingMore}
        lastElementRef={lastElementRef}
        channelById={channelById}
        selectedConversationId={selectedConversationId}
        onSelectConversation={selectConversation}
        onResolveConversation={resolveConversation}
        onUnresolveConversation={unresolveConversation}
        onRetry={retry}
      />
      <section className={styles.thread} aria-label="Message thread">
        {paneConversationId ? (
          <>
            <ConversationHeader
              conversation={selectedConversation}
              isDetailsOpen={isDetailsOpen}
              onToggleDetails={toggleDetails}
              onResolve={resolveConversation}
              onUnresolve={unresolveConversation}
            />
            <MessageThread
              conversationId={paneConversationId}
              messages={thread.messages}
              loading={thread.loading || !selectedConversation}
              error={thread.error}
              lastReadAt={selectedConversation?.readState.lastReadAt}
              onRetry={retryThread}
            />
            {selectedConversation && (
              <div className={styles.composerArea}>
                <MessageComposer
                  conversationId={selectedConversation.id}
                  service={selectedConversation.serviceType}
                  counterpartUsername={
                    getCounterpart(selectedConversation.participants).username
                  }
                  replyWindowExpiresAt={getReplyWindowExpiresAt(
                    selectedConversation,
                  )}
                />
              </div>
            )}
          </>
        ) : (
          <NoConversationSelected />
        )}
      </section>
      <DetailsPanel
        open={isDetailsOpen && Boolean(paneConversationId)}
        onClose={closeDetails}
        conversation={selectedConversation}
      />
    </div>
  )
}

function NoConversationSelected(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      className={styles.emptyThread}
    >
      <EmptyState size="small">
        <EmptyState.Icon>
          <MessagesSquareIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Select a conversation</EmptyState.Heading>
        <EmptyState.Description>
          Pick a conversation on the left to read and reply to it.
        </EmptyState.Description>
      </EmptyState>
    </Flex>
  )
}
